// src/pages/FamilyOfficeManager.jsx
// Version étendue : conserve les fonctions existantes et ajoute
// - Clients sans famille (bannière + section cartes)
// - Modals créer/rattacher
// - Rattachement + update rôle user -> user_familyoffice
// - Email depuis auth.users via Edge Function (fallback)
// ----------------------------------------------------------------

import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../lib/supabaseClient";
import {
  Users,
  PlusCircle,
  CheckCircle,
  AlertCircle,
  Filter,
  Trash2,
  Briefcase,
  UserPlus,
  Link as LinkIcon,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../styles/FamilyOfficeManager.css";

// ---------- Helpers (hors composant pour éviter des deps instables) ----------
const displayLabelForClient = (c) =>
  c?.display_name?.trim() ||
  [c?.first_name, c?.last_name].filter(Boolean).join(" ").trim() ||
  "Famille";

const ensureUniqueFamilyLabel = (base, families) => {
  const taken = new Set((families || []).map((f) => (f.label || "").toLowerCase().trim()));
  let candidate = (base || "").trim();
  if (!candidate) candidate = "Famille";
  let i = 1;
  while (taken.has(candidate.toLowerCase())) {
    i += 1;
    candidate = `${base} ${i}`;
  }
  return candidate;
};

const COPY = {
  fr: {
    sqlErr: (m) => `Erreur SQL: ${m}`,
    title: "Gestion des familles",
    subtitle: "Créez et organisez les familles de votre portefeuille",
    bannerTxt: "Ces clients vous ont donné accès à leurs collections mais ne sont pas encore rattachés à une famille.",
    pendingTitle: "Clients en attente d’organisation",
    pendingSub: "Créez une famille ou rattachez ces clients à une famille existante.",
    waiting: "En attente",
    accessGiven: "Accès donné le",
    newFamily: "Nouvelle famille",
    attach: "Rattacher à une famille",
    createChief: "Créer famille (chef)",
    createChiefTitle: "Créer une famille et définir ce client comme chef",
    createNewFamily: "Créer une nouvelle famille",
    familyNamePh: "Nom de la famille...",
    creating: "Création...",
    create: "Créer",
    notesPh: "Notes (optionnel)...",
    tip: "💡 Astuce : Après avoir créé la famille, vous pourrez y ajouter des clients",
    searchPh: "Rechercher une famille ou des notes...",
    loading: "Chargement...",
    noneFound: "Aucune famille trouvée",
    noneCreated: "Aucune famille créée",
    open: "Ouvrir",
    deleteTitle: "Supprimer la famille",
    forClient: "Pour le client",
    familyName: "Nom de la famille *",
    clientRoleInFam: "Rôle du client dans la famille *",
    ownerMain: "Propriétaire principal",
    spouse: "Conjoint",
    child: "Enfant",
    parent: "Parent",
    otherMember: "Autre membre",
    createFamilyBtn: "Créer la famille",
    clientToAttach: "Client à rattacher",
    pickFamily: "Sélectionner une famille *",
    chooseFamily: "Choisir une famille...",
    roleInFam: "Rôle dans la famille *",
    member: "Membre",
    owner: "Propriétaire",
    cancel: "Annuler",
    attaching: "Rattachement...",
    attachBtn: "Rattacher",
    errLoadPending: "Erreur lors du chargement des clients en attente",
    errCreate: "Erreur lors de la création de la famille",
    errName: "Veuillez renseigner le nom de la famille.",
    errPick: "Veuillez sélectionner une famille et un client.",
    okAttached: "Client rattaché avec succès",
    errAttach: "Erreur lors du rattachement",
    okAll: "Toutes les familles ont été créées avec leurs chefs !",
    errSome: "Certaines créations ont échoué. Vérifiez les logs.",
    notAuth: "Non authentifié",
    anon: "Client anonyme",
    emailNA: "Email non disponible",
    createdOn: "Créée le",
    managedFamilies: "Familles gérées",
    fAll: "Toutes",
    fActive: "Actives",
    fInactive: "Inactives",
    createAll: "Créer les familles pour tous",
    seeClients: "Voir les clients",
    pendingBanner: (n) => `${n} client${n > 1 ? "s" : ""} en attente d'organisation`,
    nClients: (n) => `${n} client${n > 1 ? "s" : ""}`,
    noneStatus: (active) => `Aucune famille ${active ? "active" : "inactive"}`,
    sessionErr: (m) => `Erreur session: ${m}`,
    famCreated: (l) => `Famille “${l}” créée`,
    famCreatedChief: (l, n) => `Famille “${l}” créée — ${n} défini chef de famille`,
    famCreatedAttached: (l) => `Famille “${l}” créée et client rattaché`,
    famDeleted: (l) => `Famille "${l}" supprimée`,
    deleteConfirm: (l) => `Supprimer la famille "${l}" ?\n\n⚠️ Cette action supprimera la famille mais pas les clients ni leurs collections.\nLes liens dans family_clients seront supprimés automatiquement.`,
  },
  en: {
    sqlErr: (m) => `SQL error: ${m}`,
    title: "Family management",
    subtitle: "Create and organise the families of your portfolio",
    bannerTxt: "These clients have given you access to their collections but are not yet attached to a family.",
    pendingTitle: "Clients waiting to be organised",
    pendingSub: "Create a family or attach these clients to an existing family.",
    waiting: "Waiting",
    accessGiven: "Access given on",
    newFamily: "New family",
    attach: "Attach to a family",
    createChief: "Create family (head)",
    createChiefTitle: "Create a family and set this client as its head",
    createNewFamily: "Create a new family",
    familyNamePh: "Family name...",
    creating: "Creating...",
    create: "Create",
    notesPh: "Notes (optional)...",
    tip: "💡 Tip: After creating the family, you will be able to add clients to it",
    searchPh: "Search a family or notes...",
    loading: "Loading...",
    noneFound: "No family found",
    noneCreated: "No family created",
    open: "Open",
    deleteTitle: "Delete the family",
    forClient: "For the client",
    familyName: "Family name *",
    clientRoleInFam: "Client's role in the family *",
    ownerMain: "Main owner",
    spouse: "Spouse",
    child: "Child",
    parent: "Parent",
    otherMember: "Other member",
    createFamilyBtn: "Create the family",
    clientToAttach: "Client to attach",
    pickFamily: "Select a family *",
    chooseFamily: "Choose a family...",
    roleInFam: "Role in the family *",
    member: "Member",
    owner: "Owner",
    cancel: "Cancel",
    attaching: "Attaching...",
    attachBtn: "Attach",
    errLoadPending: "Error while loading the waiting clients",
    errCreate: "Error while creating the family",
    errName: "Please enter the family name.",
    errPick: "Please select a family and a client.",
    okAttached: "Client attached successfully",
    errAttach: "Error while attaching",
    okAll: "All the families have been created with their heads!",
    errSome: "Some creations failed. Check the logs.",
    notAuth: "Not authenticated",
    anon: "Anonymous client",
    emailNA: "Email not available",
    createdOn: "Created on",
    managedFamilies: "Managed families",
    fAll: "All",
    fActive: "Active",
    fInactive: "Inactive",
    createAll: "Create the families for all",
    seeClients: "See the clients",
    pendingBanner: (n) => `${n} client${n === 1 ? "" : "s"} waiting to be organised`,
    nClients: (n) => `${n} client${n === 1 ? "" : "s"}`,
    noneStatus: (active) => `No ${active ? "active" : "inactive"} family`,
    sessionErr: (m) => `Session error: ${m}`,
    famCreated: (l) => `Family “${l}” created`,
    famCreatedChief: (l, n) => `Family “${l}” created — ${n} set as head of the family`,
    famCreatedAttached: (l) => `Family “${l}” created and client attached`,
    famDeleted: (l) => `Family "${l}" deleted`,
    deleteConfirm: (l) => `Delete the family "${l}"?\n\n⚠️ This action deletes the family but not the clients or their collections.\nThe links in family_clients will be removed automatically.`,
  },
  de: {
    sqlErr: (m) => `SQL-Fehler: ${m}`,
    title: "Familienverwaltung",
    subtitle: "Legen Sie die Familien Ihres Portfolios an und ordnen Sie sie",
    bannerTxt: "Diese Kunden haben Ihnen Zugriff auf ihre Sammlungen gewährt, sind aber noch keiner Familie zugeordnet.",
    pendingTitle: "Noch zuzuordnende Kunden",
    pendingSub: "Legen Sie eine Familie an oder ordnen Sie diese Kunden einer bestehenden Familie zu.",
    waiting: "Wartend",
    accessGiven: "Zugriff gewährt am",
    newFamily: "Neue Familie",
    attach: "Einer Familie zuordnen",
    createChief: "Familie anlegen (Oberhaupt)",
    createChiefTitle: "Eine Familie anlegen und diesen Kunden als Oberhaupt festlegen",
    createNewFamily: "Neue Familie anlegen",
    familyNamePh: "Name der Familie...",
    creating: "Wird angelegt...",
    create: "Anlegen",
    notesPh: "Notizen (optional)...",
    tip: "💡 Tipp: Nach dem Anlegen der Familie können Sie Kunden hinzufügen",
    searchPh: "Familie oder Notizen suchen...",
    loading: "Wird geladen...",
    noneFound: "Keine Familie gefunden",
    noneCreated: "Keine Familie angelegt",
    open: "Öffnen",
    deleteTitle: "Familie löschen",
    forClient: "Für den Kunden",
    familyName: "Name der Familie *",
    clientRoleInFam: "Rolle des Kunden in der Familie *",
    ownerMain: "Hauptinhaber",
    spouse: "Ehepartner",
    child: "Kind",
    parent: "Elternteil",
    otherMember: "Sonstiges Mitglied",
    createFamilyBtn: "Familie anlegen",
    clientToAttach: "Zuzuordnender Kunde",
    pickFamily: "Familie auswählen *",
    chooseFamily: "Familie wählen...",
    roleInFam: "Rolle in der Familie *",
    member: "Mitglied",
    owner: "Inhaber",
    cancel: "Abbrechen",
    attaching: "Wird zugeordnet...",
    attachBtn: "Zuordnen",
    errLoadPending: "Fehler beim Laden der noch zuzuordnenden Kunden",
    errCreate: "Fehler beim Anlegen der Familie",
    errName: "Bitte geben Sie den Namen der Familie ein.",
    errPick: "Bitte wählen Sie eine Familie und einen Kunden.",
    okAttached: "Kunde erfolgreich zugeordnet",
    errAttach: "Fehler beim Zuordnen",
    okAll: "Alle Familien wurden mit ihren Oberhäuptern angelegt!",
    errSome: "Einige Anlagen sind fehlgeschlagen. Prüfen Sie die Protokolle.",
    notAuth: "Nicht authentifiziert",
    anon: "Anonymer Kunde",
    emailNA: "E-Mail nicht verfügbar",
    createdOn: "Erstellt am",
    managedFamilies: "Verwaltete Familien",
    fAll: "Alle",
    fActive: "Aktive",
    fInactive: "Inaktive",
    createAll: "Familien für alle anlegen",
    seeClients: "Kunden ansehen",
    pendingBanner: (n) => `${n} ${n === 1 ? "Kunde" : "Kunden"} noch zuzuordnen`,
    nClients: (n) => `${n} ${n === 1 ? "Kunde" : "Kunden"}`,
    noneStatus: (active) => `Keine ${active ? "aktive" : "inaktive"} Familie`,
    sessionErr: (m) => `Sitzungsfehler: ${m}`,
    famCreated: (l) => `Familie „${l}“ angelegt`,
    famCreatedChief: (l, n) => `Familie „${l}“ angelegt — ${n} als Oberhaupt festgelegt`,
    famCreatedAttached: (l) => `Familie „${l}“ angelegt und Kunde zugeordnet`,
    famDeleted: (l) => `Familie "${l}" gelöscht`,
    deleteConfirm: (l) => `Familie "${l}" löschen?\n\n⚠️ Diese Aktion löscht die Familie, aber nicht die Kunden oder ihre Sammlungen.\nDie Verknüpfungen in family_clients werden automatisch entfernt.`,
  },
};

function useCopy() {
  const { i18n } = useTranslation();
  const lg = String(i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  return {
    tr: COPY[lg] || COPY.fr,
    locale: lg === "de" ? "de-DE" : lg === "en" ? "en-GB" : "fr-FR",
  };
}

export default function FamilyOfficeManager() {
  const navigate = useNavigate();
  const { tr, locale } = useCopy();

  // ====== ÉTATS EXISTANTS (conservés) ======
  const [families, setFamilies] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [label, setLabel] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentFOId, setCurrentFOId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ====== NOUVEAUX ÉTATS ======
  const [clientsSansFamille, setClientsSansFamille] = useState([]);
  const [clientFamilyCounts, setClientFamilyCounts] = useState({}); // {family_id: count}
  const [showCreateFamilyModal, setShowCreateFamilyModal] = useState(false);
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [selectedPendingClient, setSelectedPendingClient] = useState(null);

  const [newFamilyName, setNewFamilyName] = useState("");
  const [newFamilyRole, setNewFamilyRole] = useState("owner");
  const [selectedFamilyId, setSelectedFamilyId] = useState("");
  const [attachRole, setAttachRole] = useState("member");

  // ====== UTILS ======
  const debug = (lbl, data) => console.log(`🔍 [FO-Manager] ${lbl}:`, data);
  const showSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };
  const showError = (message) => {
    setError(message);
    setTimeout(() => setError(null), 7000);
  };

  // ====== AUTH FO ID ======
  const getCurrentFOId = useCallback(async () => {
    const { data: session, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw new Error(tr.sessionErr(sessionError.message));
    const foId = session?.session?.user?.id;
    if (!foId) throw new Error(tr.notAuth);
    setCurrentFOId(foId);
    return foId;
  }, [tr]);

  // ====== FAMILIES (conserve votre logique existante) ======
  const fetchFamilies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const foId = currentFOId || (await getCurrentFOId());

      let q = supabase
        .from("families")
        .select("*")
        .eq("family_office_id", foId)
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") q = q.eq("status", statusFilter);

      const { data, error: fetchError } = await q;
      if (fetchError) throw new Error(tr.sqlErr(fetchError.message));

      // Comptage des clients via family_clients pour chaque famille
      const counts = {};
      const familiesWithClients = await Promise.all(
        (data || []).map(async (f) => {
          const { data: fc, error: fcErr } = await supabase
            .from("family_clients")
            .select("id, client_id")
            .eq("family_id", f.id);

          if (fcErr) debug(`Erreur family_clients ${f.id}`, fcErr);

          const client_count = fc?.length || 0;
          counts[f.id] = client_count;
          return { ...f, client_count };
        })
      );

      setClientFamilyCounts(counts);
      setFamilies(familiesWithClients);
      setFiltered(familiesWithClients);
    } catch (err) {
      console.error("❌ Erreur fetchFamilies:", err);
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentFOId, statusFilter, getCurrentFOId, tr]);

  // ====== LISTE CLIENTS SANS FAMILLE ======
  // Détection : clients qui ont donné accès au FO via collection_permissions
  // et qui ne figurent pas encore dans family_clients
  const fetchClientsSansFamille = useCallback(async () => {
    try {
      const foId = currentFOId || (await getCurrentFOId());

      // 1) Permissions d'accès données au FO
      const { data: perms, error: permErr } = await supabase
        .from("collection_permissions")
        .select("client_id")
        .eq("grantee_id", foId)
        .eq("can_view", true);

      if (permErr) throw permErr;

      const uniqueClientIds = [...new Set((perms || []).map((p) => p.client_id).filter(Boolean))];
      if (uniqueClientIds.length === 0) {
        setClientsSansFamille([]);
        return;
      }

      // 2) Clients déjà rattachés ?
      const { data: familyLinks, error: linkErr } = await supabase
        .from("family_clients")
        .select("client_id")
        .in("client_id", uniqueClientIds);

      if (linkErr) throw linkErr;

      const attached = new Set((familyLinks || []).map((l) => l.client_id));
      const pendingIds = uniqueClientIds.filter((id) => !attached.has(id));
      if (pendingIds.length === 0) {
        setClientsSansFamille([]);
        return;
      }

      // 3) Profils basiques (nom/société)
      const { data: profiles, error: profileErr } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, company, created_at, role")
        .in("id", pendingIds);

      if (profileErr) throw profileErr;

      // 4) Emails depuis auth.users via Edge Function (sécurisée côté serveur)
      //    Fonction: fo-get-user-emails { ids: string[] } -> [{id, email}]
      let emailMap = {};
      try {
        const { data: fnData, error: fnErr } = await supabase.functions.invoke("fo-get-user-emails", {
          body: { ids: pendingIds },
        });
        if (fnErr) throw fnErr;
        (fnData || []).forEach((u) => {
          emailMap[u.id] = u.email;
        });
      } catch (e) {
        debug("Edge Function email indisponible (fallback)", e?.message || e);
      }

      const pendingClients = (profiles || []).map((p) => ({
        id: p.id,
        display_name:
          p.company || [p.first_name, p.last_name].filter(Boolean).join(" ") || tr.anon,
        email: emailMap[p.id] || tr.emailNA,
        created_at: p.created_at,
        role: p.role || "user",
      }));

      setClientsSansFamille(pendingClients);
    } catch (err) {
      console.error("❌ Erreur fetchClientsSansFamille:", err);
      showError(tr.errLoadPending);
    }
  }, [currentFOId, getCurrentFOId, tr]);

  // ====== CRÉATION “MANUELLE” (bloc existant : input label + notes) ======
  const createFamily = useCallback(async () => {
    if (!label.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const foId = currentFOId || (await getCurrentFOId());

      const { data: newFam, error: famErr } = await supabase
        .from("families")
        .insert({
          label: label.trim(),
          notes: notes?.trim() || null,
          status: "active",
          family_office_id: foId,
        })
        .select()
        .single();

      if (famErr) throw famErr;

      showSuccess(tr.famCreated(newFam.label));
      setLabel("");
      setNotes("");
      await fetchFamilies();
    } catch (err) {
      console.error("❌ Erreur createFamily:", err);
      showError(err.message || tr.errCreate);
    } finally {
      setLoading(false);
    }
  }, [label, notes, currentFOId, getCurrentFOId, fetchFamilies, tr]);

  // ====== CRÉER FAMILLE POUR UN CLIENT (Quick) ======
  // 🎯 Création instantanée : crée une famille et met ce client en chef (owner + is_primary)
  const handleQuickCreateFamily = useCallback(
    async (client) => {
      if (!client?.id) return;
      setProcessingAction(true);
      try {
        const foId = currentFOId || (await getCurrentFOId());

        // Nom automatique unique: "Famille {Nom Affiché}"
        const base = `Famille ${displayLabelForClient(client)}`;
        const labelAuto = ensureUniqueFamilyLabel(base, families);

        // 1) Créer la famille (renseigne le client_profile_id immédiatement)
        const { data: fam, error: famErr } = await supabase
          .from("families")
          .insert({
            label: labelAuto,
            family_office_id: foId,
            client_profile_id: client.id, // chef connu au niveau famille
            status: "active",
          })
          .select()
          .single();

        if (famErr) throw famErr;

        // 2) Lier le client comme OWNER + is_primary
        const { error: linkErr } = await supabase.from("family_clients").insert({
          family_id: fam.id,
          client_id: client.id,
          role: "owner",
          is_primary: true,
        });
        if (linkErr) throw linkErr;

        // 3) Mettre à jour le rôle du profil
        const { error: roleErr } = await supabase
          .from("profiles")
          .update({ role: "user_familyoffice" })
          .eq("id", client.id);
        if (roleErr) console.warn("Update rôle (non bloquant):", roleErr);

        showSuccess(tr.famCreatedChief(fam.label, client.display_name));
        await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
      } catch (e) {
        console.error("Quick create error:", e);
        showError(tr.errCreate);
      } finally {
        setProcessingAction(false);
      }
    },
    [currentFOId, getCurrentFOId, families, fetchFamilies, fetchClientsSansFamille, tr]
  );

  // ====== CRÉER FAMILLE POUR UN CLIENT (Modal) ======
  const handleCreateFamily = useCallback(async () => {
    if (!newFamilyName.trim() || !selectedPendingClient) {
      showError(tr.errName);
      return;
    }
    setProcessingAction(true);
    try {
      const foId = currentFOId || (await getCurrentFOId());

      // 1) Créer la famille
      const { data: newFam, error: famErr } = await supabase
        .from("families")
        .insert({
          label: newFamilyName.trim(),
          family_office_id: foId,
          status: "active",
          notes: null,
        })
        .select()
        .single();

      if (famErr) throw famErr;

      // 2) Lier le client
      const { error: linkErr } = await supabase.from("family_clients").insert({
        family_id: newFam.id,
        client_id: selectedPendingClient.id,
        role: newFamilyRole,
        is_primary: true,
      });

      if (linkErr) throw linkErr;

      // 3) Mettre à jour le rôle
      const { error: roleErr } = await supabase
        .from("profiles")
        .update({ role: "user_familyoffice" })
        .eq("id", selectedPendingClient.id);
      if (roleErr) debug("⚠️ Update rôle échoué (non bloquant)", roleErr);

      showSuccess(tr.famCreatedAttached(newFam.label));

      // Reset + refresh
      setShowCreateFamilyModal(false);
      setNewFamilyName("");
      setNewFamilyRole("owner");
      setSelectedPendingClient(null);

      await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
    } catch (err) {
      console.error("❌ Erreur création famille:", err);
      showError(tr.errCreate);
    } finally {
      setProcessingAction(false);
    }
  }, [
    newFamilyName,
    selectedPendingClient,
    newFamilyRole,
    currentFOId,
    getCurrentFOId,
    fetchFamilies,
    fetchClientsSansFamille,
    tr,
  ]);

  // ====== RATTACHER À FAMILLE EXISTANTE (Modal) ======
  const handleAttachToFamily = useCallback(async () => {
    if (!selectedFamilyId || !selectedPendingClient) {
      showError(tr.errPick);
      return;
    }
    setProcessingAction(true);
    try {
      // 1) Lier le client
      const { error: linkErr } = await supabase.from("family_clients").insert({
        family_id: selectedFamilyId,
        client_id: selectedPendingClient.id,
        role: attachRole,
        is_primary: false,
      });
      if (linkErr) throw linkErr;

      // 2) Mettre à jour le rôle
      const { error: roleErr } = await supabase
        .from("profiles")
        .update({ role: "user_familyoffice" })
        .eq("id", selectedPendingClient.id);
      if (roleErr) debug("⚠️ Update rôle échoué (non bloquant)", roleErr);

      showSuccess(tr.okAttached);
      setShowAttachModal(false);
      setSelectedFamilyId("");
      setAttachRole("member");
      setSelectedPendingClient(null);

      await Promise.all([fetchFamilies(), fetchClientsSansFamille()]);
    } catch (err) {
      console.error("❌ Erreur rattachement:", err);
      showError(tr.errAttach);
    } finally {
      setProcessingAction(false);
    }
  }, [
    selectedFamilyId,
    selectedPendingClient,
    attachRole,
    fetchFamilies,
    fetchClientsSansFamille,
    tr,
  ]);

  // ====== SUPPRESSION / OUVERTURE (conservé) ======
  const deleteFamily = useCallback(
    async (familyId, familyLabel) => {
      if (
        !window.confirm(
          tr.deleteConfirm(familyLabel)
        )
      ) {
        return;
      }
      setDeletingId(familyId);
      try {
        const { error: deleteError } = await supabase.from("families").delete().eq("id", familyId);
        if (deleteError) throw deleteError;
        showSuccess(tr.famDeleted(familyLabel));
        await fetchFamilies();
      } catch (err) {
        console.error("❌ Erreur suppression:", err);
        showError(err.message);
      } finally {
        setDeletingId(null);
      }
    },
    [fetchFamilies, tr]
  );

  const openFamily = useCallback((familyId) => navigate(`/family-office/families/${familyId}`), [navigate]);

  // ====== EFFECTS ======
  // ✅ Fix ESLint: depend on the callback itself, not just statusFilter
  useEffect(() => {
    fetchFamilies();
  }, [fetchFamilies]);

  useEffect(() => {
    // Recherche locale (conservée)
    if (!families.length) {
      setFiltered([]);
      return;
    }
    if (!query.trim()) {
      setFiltered(families);
      return;
    }
    const q = query.toLowerCase();
    setFiltered(
      families.filter(
        (f) =>
          (f.label || "").toLowerCase().includes(q) || (f.notes || "").toLowerCase().includes(q)
      )
    );
  }, [query, families]);

  useEffect(() => {
    // Charger la liste “en attente”
    fetchClientsSansFamille();
  }, [fetchClientsSansFamille]);

  // ====== RENDER ======
  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <div className="border-b border-[#E8E8E8] bg-white">
        <div className="max-w-7xl mx-auto px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-light text-[#1A1A1A] mb-2">{tr.title}</h1>
              <p className="text-sm text-[#666]">{tr.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-12 space-y-8">
        {/* Messages */}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded p-4 flex items-start gap-3">
            <CheckCircle className="text-emerald-600 shrink-0" size={20} />
            <p className="text-sm text-emerald-800">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded p-4 flex items-start gap-3">
            <AlertCircle className="text-red-600 shrink-0" size={20} />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* 🟧 BANNIÈRE ALERTE : Clients en attente */}
        {clientsSansFamille.length > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500 rounded-lg shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center">
                    <AlertCircle className="text-white" size={24} />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-light text-[#1A1A1A] mb-2">
                    ⚠️ {tr.pendingBanner(clientsSansFamille.length)}
                  </h3>
                  <p className="text-sm text-[#666] mb-4 leading-relaxed">
                    {tr.bannerTxt}
                  </p>

                  {clientsSansFamille.length > 0 && (
                    <div className="mt-4">
                      <button
                        onClick={async () => {
                          setProcessingAction(true);
                          try {
                            for (const c of clientsSansFamille) {
                              await handleQuickCreateFamily(c);
                            }
                            showSuccess(tr.okAll);
                          } catch (e) {
                            console.error(e);
                            showError(tr.errSome);
                          } finally {
                            setProcessingAction(false);
                          }
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
                        disabled={processingAction}
                      >
                        {tr.createAll} ({clientsSansFamille.length})
                      </button>
                    </div>
                  )}

                  <button
                    onClick={() =>
                      document.getElementById("clients-sans-famille")?.scrollIntoView({ behavior: "smooth" })
                    }
                    className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors"
                  >
                    <Users size={16} />
                    {tr.seeClients} ({clientsSansFamille.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 🟧 SECTION : Clients en attente d'organisation */}
        {clientsSansFamille.length > 0 && (
          <div id="clients-sans-famille" className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
            <div className="px-8 py-6 border-b border-[#E8E8E8]">
              <h2 className="text-lg font-light text-[#1A1A1A]">{tr.pendingTitle}</h2>
              <p className="text-sm text-[#666]">{tr.pendingSub}</p>
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {clientsSansFamille.map((client) => (
                <div
                  key={client.id}
                  className="bg-white border-2 border-orange-200 rounded-lg p-6 hover:border-orange-400 transition-all shadow-sm"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-400 to-amber-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-light text-lg">
                        {(client.display_name?.[0] || "?").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-normal text-[#1A1A1A] mb-1 truncate">{client.display_name}</h3>
                      <p className="text-xs text-[#666] truncate">{client.email}</p>
                      <span className="inline-block mt-2 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                        {tr.waiting}
                      </span>
                    </div>
                  </div>

                  <div className="mb-4 pb-4 border-b border-[#E8E8E8]">
                    <div className="text-xs text-[#999]">{tr.accessGiven}</div>
                    <div className="text-sm text-[#1A1A1A]">
                      {client.created_at
                        ? new Date(client.created_at).toLocaleDateString(locale, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })
                        : "—"}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSelectedPendingClient(client);
                        setShowCreateFamilyModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors"
                    >
                      <UserPlus size={16} />
                      {tr.newFamily}
                    </button>

                    <button
                      onClick={() => {
                        setSelectedPendingClient(client);
                        setShowAttachModal(true);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border border-[#E8E8E8] text-[#1A1A1A] text-sm rounded hover:border-[#1A1A1A] transition-colors"
                    >
                      <LinkIcon size={16} />
                      {tr.attach}
                    </button>

                    <button
                      onClick={() => handleQuickCreateFamily(client)}
                      disabled={processingAction}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-600 text-white text-sm rounded hover:bg-orange-700 transition-colors disabled:opacity-50"
                      title={tr.createChiefTitle}
                    >
                      {tr.createChief}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ===== Création (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-6">
            <h2 className="text-lg font-light text-[#1A1A1A]">{tr.createNewFamily}</h2>
          </div>

          <div className="px-8 py-8 space-y-4">
            <div className="flex gap-4">
              <input
                type="text"
                placeholder={tr.familyNamePh}
                className="flex-1 border border-[#E8E8E8] rounded px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A]"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !loading) createFamily();
                }}
                disabled={loading}
              />

              <button
                onClick={createFamily}
                disabled={loading || !label.trim()}
                className={`flex items-center gap-2 px-6 py-3 rounded text-sm font-light transition-all shrink-0 ${
                  loading || !label.trim()
                    ? "bg-[#E8E8E8] text-[#999] cursor-not-allowed"
                    : "bg-[#1A1A1A] text-white hover:bg-[#333]"
                }`}
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{tr.creating}</span>
                  </>
                ) : (
                  <>
                    <PlusCircle size={16} />
                    <span>{tr.create}</span>
                  </>
                )}
              </button>
            </div>

            <textarea
              placeholder={tr.notesPh}
              className="w-full border border-[#E8E8E8] rounded px-4 py-3 text-sm text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:ring-2 focus:ring-[#1A1A1A] resize-none"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading}
            />

            <p className="text-xs text-[#999] font-light">{tr.tip}</p>
          </div>
        </div>

        {/* ===== Recherche/Filtres (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="px-8 py-6 space-y-4">
            <input
              className="w-full border-0 outline-none text-sm text-[#1A1A1A] placeholder:text-[#999]"
              placeholder={tr.searchPh}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />

            <div className="flex items-center gap-3 pt-4 border-t border-[#E8E8E8]">
              <Filter size={16} className="text-[#999]" />
              <div className="flex gap-2">
                {["all", "active", "inactive"].map((status) => (
                  <button
                    key={status}
                    onClick={() => setStatusFilter(status)}
                    className={`px-3 py-1 rounded text-xs font-light transition-all ${
                      statusFilter === status ? "bg-[#1A1A1A] text-white" : "bg-[#F5F5F5] text-[#666] hover:bg-[#E8E8E8]"
                    }`}
                  >
                    {status === "all" ? tr.fAll : status === "active" ? tr.fActive : tr.fInactive}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ===== Liste familles (existant) ===== */}
        <div className="bg-white rounded border border-[#E8E8E8] shadow-sm overflow-hidden">
          <div className="border-b border-[#E8E8E8] px-8 py-6">
            <h2 className="text-lg font-light text-[#1A1A1A]">
              {tr.managedFamilies} <span className="ml-2 text-[#999]">({filtered.length})</span>
            </h2>
          </div>

          <div className="divide-y divide-[#E8E8E8]">
            {loading ? (
              <div className="px-8 py-12 text-center">
                <div className="inline-block w-8 h-8 border-2 border-[#E8E8E8] border-t-[#1A1A1A] rounded-full animate-spin" />
                <p className="mt-4 text-sm text-[#999]">{tr.loading}</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-8 py-12 text-center">
                <Users className="mx-auto mb-4 text-[#CCC]" size={48} strokeWidth={1} />
                <p className="text-sm text-[#999] font-light">
                  {query
                    ? tr.noneFound
                    : statusFilter !== "all"
                    ? tr.noneStatus(statusFilter === "active")
                    : tr.noneCreated}
                </p>
              </div>
            ) : (
              filtered.map((f) => (
                <div
                  key={f.id}
                  className="px-8 py-6 hover:bg-[#FAFAF8] transition-colors flex items-center justify-between gap-6"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="text-base font-light text-[#1A1A1A] truncate">{f.label}</h3>
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-light ${
                          f.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
                        }`}
                      >
                        {f.status}
                      </span>
                    </div>

                    <div className="flex items-center gap-4 text-xs text-[#999]">
                      <span className="flex items-center gap-1">
                        <Briefcase size={12} />
                        {tr.nClients(clientFamilyCounts[f.id] ?? f.client_count ?? 0)}
                      </span>
                      {f.created_at && (
                        <span>
                          {tr.createdOn}{" "}
                          {new Date(f.created_at).toLocaleDateString(locale, {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}
                        </span>
                      )}
                    </div>

                    {f.notes && <p className="mt-2 text-xs text-[#666] italic truncate">{f.notes}</p>}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      className="px-5 py-2 text-sm font-light rounded border border-[#E8E8E8] hover:bg-[#1A1A1A] hover:text-white hover:border-[#1A1A1A] transition-all"
                      onClick={() => openFamily(f.id)}
                    >
                      {tr.open}
                    </button>

                    <button
                      className={`p-2 text-sm font-light rounded border border-[#E8E8E8] hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-all ${
                        deletingId === f.id ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      onClick={() => deleteFamily(f.id, f.label)}
                      disabled={deletingId === f.id}
                      title={tr.deleteTitle}
                    >
                      {deletingId === f.id ? (
                        <div className="w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* MODAL : Créer une nouvelle famille pour ce client */}
      {showCreateFamilyModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !processingAction && setShowCreateFamilyModal(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#E8E8E8]">
              <h3 className="text-xl font-light text-[#1A1A1A]">{tr.createNewFamily}</h3>
              <button
                onClick={() => !processingAction && setShowCreateFamilyModal(false)}
                className="text-[#999] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {selectedPendingClient && (
                <div className="p-4 bg-gray-50 rounded border border-[#E8E8E8]">
                  <div className="text-xs text-[#999] mb-1">{tr.forClient}</div>
                  <div className="text-sm font-normal text-[#1A1A1A]">{selectedPendingClient.display_name}</div>
                  <div className="text-xs text-[#666]">{selectedPendingClient.email}</div>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#666] mb-2">{tr.familyName}</label>
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  placeholder="Ex: Famille Dupont"
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                />
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">{tr.clientRoleInFam}</label>
                <select
                  value={newFamilyRole}
                  onChange={(e) => setNewFamilyRole(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="owner">{tr.ownerMain}</option>
                  <option value="spouse">{tr.spouse}</option>
                  <option value="child">{tr.child}</option>
                  <option value="parent">{tr.parent}</option>
                  <option value="member">{tr.otherMember}</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E8E8E8]">
              <button
                onClick={() => setShowCreateFamilyModal(false)}
                className="px-4 py-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                {tr.cancel}
              </button>
              <button
                onClick={handleCreateFamily}
                disabled={!newFamilyName.trim() || processingAction}
                className="px-6 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingAction ? tr.creating : tr.createFamilyBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL : Rattacher à une famille existante */}
      {showAttachModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !processingAction && setShowAttachModal(false)}
        >
          <div className="bg-white rounded-lg max-w-md w-full shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-[#E8E8E8]">
              <h3 className="text-xl font-light text-[#1A1A1A]">{tr.attach}</h3>
              <button
                onClick={() => !processingAction && setShowAttachModal(false)}
                className="text-[#999] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {selectedPendingClient && (
                <div className="p-4 bg-gray-50 rounded border border-[#E8E8E8]">
                  <div className="text-xs text-[#999] mb-1">{tr.clientToAttach}</div>
                  <div className="text-sm font-normal text-[#1A1A1A]">{selectedPendingClient.display_name}</div>
                  <div className="text-xs text-[#666]">{selectedPendingClient.email}</div>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#666] mb-2">{tr.pickFamily}</label>
                <select
                  value={selectedFamilyId}
                  onChange={(e) => setSelectedFamilyId(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="">{tr.chooseFamily}</option>
                  {families.map((fam) => (
                    <option key={fam.id} value={fam.id}>
                      {fam.label} ({(clientFamilyCounts[fam.id] ?? fam.client_count ?? 0)} membre
                      {(clientFamilyCounts[fam.id] ?? fam.client_count ?? 0) > 1 ? "s" : ""})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm text-[#666] mb-2">{tr.roleInFam}</label>
                <select
                  value={attachRole}
                  onChange={(e) => setAttachRole(e.target.value)}
                  className="w-full px-4 py-3 border border-[#E8E8E8] rounded text-sm focus:outline-none focus:border-[#1A1A1A]"
                  disabled={processingAction}
                >
                  <option value="member">{tr.member}</option>
                  <option value="owner">{tr.owner}</option>
                  <option value="spouse">{tr.spouse}</option>
                  <option value="child">{tr.child}</option>
                  <option value="parent">{tr.parent}</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-6 border-t border-[#E8E8E8]">
              <button
                onClick={() => setShowAttachModal(false)}
                className="px-4 py-2 text-sm text-[#666] hover:text-[#1A1A1A] transition-colors"
                disabled={processingAction}
              >
                {tr.cancel}
              </button>
              <button
                onClick={handleAttachToFamily}
                disabled={!selectedFamilyId || processingAction}
                className="px-6 py-2 bg-[#1A1A1A] text-white text-sm rounded hover:bg-[#333] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingAction ? tr.attaching : tr.attachBtn}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
